import { App, Events, User } from "app-interfaces";
import i18next from "i18next";
import { DateTime } from "luxon";
import React from "react";
import Rollbar from "rollbar";

import { LoadingModal, Modal } from "../components/Modals";
import ServerApi, { ApiError, ServerApiContext } from "../server-api";
import { isIso639Code, localStorage, saveLanguageToLocalStorage } from "../storage";
import { EventType } from "../types/StaticTypes";
import { Optional } from "../types/utils";
import { nowAsDateTimeWithZ, toDateOnly } from "../utils/time-utils";
import AppContext, { InitialState } from "./AppContext";

type Props = { rollbarInstance: Rollbar };

const noop = () => {};
const identity = (e: any) => e;

export default class AppProvider extends React.Component<Props, App.State> {
  private readonly api: ServerApi;
  private readonly rollbarInstance: Rollbar;

  constructor(props: Props) {
    super(props);
    this.api = new ServerApi({ onError: this.handleServerError });
    this.rollbarInstance = props.rollbarInstance;

    this.state = {
      ...InitialState,

      Methods: {
        // State modifiers
        setLoadingState: this.setLoadingState,

        handleAbort: this.handleAbort,
        setAppState: this.setAppState,
        // Auth
        handleLogin: this.handleLogin,
        handleLogout: this.handleLogout,
        onLogIn: this.onLogIn,
        recoverPass: this.recoverPass,
        // User
        getProfile: this.api.getProfile,
        updateProfile: this.updateProfile,
        updateEmail: this.api.updateEmail,
        updatePass: this.api.updatePassword,
        // Events
        getEventsAndUpdateState: this.getEventsAndUpdateState,
        getEventDetails: this.getEventDetails,
        getEvents: this.getEvents,
        postEvents: this.postEvents,
        inviteClientsToEvent: this.inviteClientsToEvent,
        putEvents: this.putEvents,
        delEvents: this.delEvents,
        // Clients
        getCountries: this.api.getCountries,
        getClients: this.getClients,
        postClients: this.postClients,
        putClient: this.putClient,
        inviteClient: this.inviteClient,
        inviteClientBooking: this.inviteClientBooking,
        forgetClient: this.forgetClient,
        updateImage: this.updateImage,
        // Statistics
        getStats: this.getStats,
        exportStats: this.exportStats,
        // Exercises
        getExercises: this.getExercises,

        addExercises: this.addExercises,
        updateExercises: this.updateExercises,
        updateExerciseImage: this.updateExerciseImage,
        // Workouts
        getWorkouts: this.getWorkouts,
        addWorkouts: this.addWorkouts,
        updateWorkouts: this.updateWorkouts,
        shareWorkouts: this.shareWorkouts,
        // Schedule
        getScheduledEvents: this.getScheduledEvents,
        getSessionAvailability: this.getSessionAvailability,
        updateScheduledWorkout: this.updateScheduledWorkout,
        shareSession: this.shareSession,
        // Dashboard
        getDashboardEvents: this.getDashboardEvents,
        // Contact Us
        contactUs: this.contactUs,
      },
    };
  }

  /** Properties */

  // State Initialization
  state: App.State = InitialState;

  /**
   * Handle User Unauthorized & Errors
   */
  private handleServerError: (error: ApiError) => Promise<void> = (error) =>
    handleServerError(error, this.setAppState.bind(this), this.rollbarInstance);

  componentDidMount() {
    if (!this.state.authenticated && this.api?.token?.length) {
      return this.onLogIn().catch((err) => {
        console.error("Failed logging in using token:", err.message);
      });
    }
    this.clearLoadingFlag();
  }

  /* Cleanup on unmount */
  componentWillUnmount = () => {
    this.handleAbort();
  };

  render() {
    return (
      <AppContext.Provider value={{ ...this.state }}>
        <ServerApiContext.Provider value={this.api}>
          {this.props.children}
          {/* Modals */}
          <Modal
            open={this.state.showModal}
            message={this.state.modalMessage}
            title={this.state.modalTitle}
            onClose={() =>
              this.setState({
                showModal: false,
              })
            }
          />
          <LoadingModal
            show={this.state.showLoadingModal}
            onHide={() =>
              this.setState({
                showLoadingModal: false,
              })
            }
          />
        </ServerApiContext.Provider>
      </AppContext.Provider>
    );
  }

  /* Methods */

  private clearLoadingFlag() {
    return this.setState({ showLoadingModal: false });
  }

  /**
   * Update State via Child component
   */
  private setAppState: App.Methods["setAppState"] = (params) => {
    if (params.metric || params.metric === false) {
      localStorage.setItem("metric", `${params.metric}`);
    }
    const props = { ...params, showLoadingModal: false };
    return this.setState((prevState) => ({ ...prevState, ...props }));
  };
  /**
   * Show loading spinner and block UI
   */
  private setLoadingState: App.Methods["setLoadingState"] = () => {
    return this.setState({ showLoadingModal: true });
  };

  /**
   * Abort api calls on unmount
   */
  private handleAbort: App.Methods["handleAbort"] = () => {
    return this.api.abort();
  };

  /* Auth */

  /**
   * Handle User Login Form Submit Action
   */
  private handleLogin: App.Methods["handleLogin"] = async (user) => {
    performance.mark("loginStart");
    this.setLoadingState();
    try {
      const data = await this.api.loginUser(user);
      const { token } = data;
      this.api.token = token;

      localStorage.setItem("token", token);
      localStorage.setItem("metric", `${this.state.metric}`);

      performance.measure("time spent on login", "loginStart");

      // need to catch any async errors that might arise
      // typically HTTP 402 on expired subscriptions
      await this.onLogIn();
    } catch (err) {
      this.clearLoadingFlag();
    }
  };
  /**
   * Check Local Storage for token on Reload
   */
  private onLogIn = async () => {
    performance.mark("onLogInStart");
    if (!this.state.showLoadingModal) this.setLoadingState();

    const profileLoading = this.api.getProfile().then((userProfile: User.IProfile) => {
      performance.measure("profile loading", "onLogInStart");
      this.setCompanyId(userProfile.companies);
      const { settings, user } = userProfile;
      AppProvider.setLanguage(settings.language);

      this.rollbarInstance.configure({
        payload: { person: user },
      });

      return userProfile;
    });

    // TODO #performance: move to IndexedDB and use that for lookups
    const countryLoading = this.api.getCountries().then((countryData) => {
      performance.measure("country loading", "onLogInStart");
      localStorage.setItem("countries", JSON.stringify(countryData));
      return countryData;
    });

    // TODO #performance: cache locally in IndexedDB and use that for lookups
    const exercisesLoading = this.getExercises().then(identity, identity);
    const workoutsLoading = this.getWorkouts().then(identity, identity);

    const localMetric = localStorage.getItem("metric");
    const metric = localMetric ? JSON.parse(localMetric) : this.state.metric;
    localStorage.setItem("metric", `${metric}`);

    const serializedCountries = localStorage.getItem("countries");

    this.setLoadingState();

    // We do not need to wait around for these to finish
    const catalogFetches = Promise.all([exercisesLoading, workoutsLoading]);
    catalogFetches.then(() => {
      performance.measure("exercises and workouts loaded", "onLogInStart");
    });

    Promise.all([catalogFetches, countryLoading]).catch((fetchError) => {
      console.error("Failed loading a background call:", fetchError.message);
    });

    // All fetch calls now run in parallel above, in a non-blocking fashion
    const data = await profileLoading;
    const clientLoading = this.getClients();

    this.getEventsAndUpdateState().then(noop, noop);

    const clients = await clientLoading;
    const { countries } = serializedCountries
      ? JSON.parse(serializedCountries)
      : await countryLoading;

    return this.setAppState({
      ...data,
      metric,
      clients,
      countries,
      authenticated: true,
    });
  };

  private static setLanguage(language?: string) {
    if (!language || !isIso639Code(language)) return;

    saveLanguageToLocalStorage(language);
    return i18next.changeLanguage(language);
  }

  private setCompanyId(companies: { id: string }[]) {
    this.api.companyId = companies.length > 0 ? companies[0].id : null;
  }

  /**
   * Handle Logout Action
   */
  private handleLogout: App.Methods["handleLogout"] = async (token) => {
    this.setLoadingState();
    await this.api.logoutUser(token);
    localStorage.clear();
    return this.setAppState(InitialState);
  };
  /**
   * Recover password
   */
  private recoverPass: App.Methods["recoverPass"] = async (email) => {
    const response = await this.api.recoverPass(email);
    if (response) {
      const { message } = response;
      return this.setAppState({
        showLoadingModal: false,
        showModal: true,
        modalMessage: message,
        modalTitle: i18next.t("Recover password"),
      });
    }
  };

  /**
   * Get events
   */
  private getEventsAndUpdateState = async () => {
    const now = nowAsDateTimeWithZ();
    const [lunch, blocked] = await Promise.all([
      /**
       * For "Lunch" events "from" and "to" dates are set to now, because we want the current settings.
       * The API will return the latest "Lunch" event, even if it was created before the "from" date.
       * See API docs for more info.
       */
      this.api.getEvents({
        from: now,
        to: now,
        types: [EventType.Lunch],
      }),

      /**
       * Show blocked events 3 months in the past from now, and 2 years in the future from now, PF-254.
       */
      this.api.getEvents({
        from: toDateOnly(DateTime.local().minus({ months: 3 })),
        to: toDateOnly(DateTime.local().plus({ years: 2 })),
        types: [EventType.BlockedDays],
      }),
    ]);
    (this.state.events.lunch = lunch.events).blocked_days = blocked.events;

    if (/clients\/sessions/.test(window.location.href)) return;
    return this.clearLoadingFlag();
  };

  /**
   * Get specific event by ID
   */
  private getEventDetails: App.Methods["getEventDetails"] = (id) => {
    return this.api.getEventDetails(id);
  };

  /**
   * Get specific client's events
   */
  private getEvents: App.Methods["getEvents"] = async (params, signal) => {
    const { events } = await this.api.getEvents(params, signal);
    if (events) return events;
  };
  /**
   * Add new event
   */
  private postEvents: App.Methods["postEvents"] = async (
    params: Optional<Events.RequestServerEvent, "id">
  ) => {
    if (!this.state.showLoadingModal && !/clients\/sessions/.test(window.location.href)) {
      this.setLoadingState();
    }

    const { id } = await this.api.postEvents(params);
    await this.getEventsAndUpdateState();
    return id;
  };
  /**
   * Invite Clients to Events
   * @param id string
   */
  private inviteClientsToEvent: App.Methods["inviteClientsToEvent"] = async (id) => {
    const response = await this.api.inviteClientsToEvent(id);
    if (response.ok) {
      return await response.json();
    }
  };

  /**
   * Update existing event
   */
  private putEvents: App.Methods["putEvents"] = async (params, id) => {
    if (!this.state.showLoadingModal && !/clients\/sessions/.test(window.location.href))
      this.setLoadingState();
    await this.api.putEvents(params, id);
    await this.getEventsAndUpdateState();
  };
  /**
   * Update session settings
   */
  private updateProfile: App.Methods["updateProfile"] = async (params) => {
    if (!this.state.user || !this.state.settings) return;

    this.setLoadingState();
    if (!params.session_length || params.session_length === 0)
      params.session_length = this.state.settings.session_length;
    if (!params.session_length_long || params.session_length_long === 0)
      params.session_length_long = this.state.settings.session_length_long;
    if (!params.session_interval || params.session_interval === 0)
      params.session_interval = this.state.settings.session_interval;
    const query = {
      settings: params,
    };

    const profile = await this.api.updateProfile(query);
    return this.setAppState({ ...profile });
  };
  /**
   * Delete existing event
   */
  private delEvents: App.Methods["delEvents"] = async (id) => {
    return await this.api.delEvent(id);
  };

  /* Clients */

  /**
   * Get Clients
   */
  private getClients: App.Methods["getClients"] = async () => {
    this.setLoadingState();
    const { customers } = await this.api.getClients();
    return customers;
  };
  /**
   * Add Client
   */
  private postClients: App.Methods["postClients"] = async (params) => {
    this.setLoadingState();
    await this.api.postClients(params);
    this.fetchAndUpdateClients().then(noop, noop); // don't block updating
  };

  private async fetchAndUpdateClients() {
    const clients = await this.getClients();
    return this.setAppState({ clients });
  }

  /**
   * Update Client
   */
  private putClient: App.Methods["putClient"] = async (params, id) => {
    this.setLoadingState();
    await this.api.putClient(params, id);
    const clients = await this.getClients();
    return this.setAppState({ clients });
  };
  /**
   * Invite Existing Client
   */
  private inviteClient: App.Methods["inviteClient"] = async (email) => {
    this.setLoadingState();
    await this.api.inviteClient(email);
    const clients = await this.getClients();
    return this.setAppState({
      clients,
    });
  };
  /**
   * Invite Client to Booking
   */
  private inviteClientBooking: App.Methods["inviteClientBooking"] = async (id) => {
    this.setLoadingState();
    await this.api.inviteClientBooking(id);
    const clients = await this.getClients();
    return this.setAppState({
      clients,
      modalTitle: i18next.t("Success"),
      modalMessage: i18next.t("Client invited successfully!"),
      showModal: true,
    });
  };
  /**
   * Add/Update Client Profile Image
   */
  private updateImage: App.Methods["updateImage"] = async (newImage) => {
    const { image } = await this.api.updateImage(newImage);
    return image;
  };
  /**
   * Forget Client
   */
  private forgetClient: App.Methods["forgetClient"] = async (id, callback) => {
    this.setLoadingState();
    await this.api.forgetClient(id);
    const clients = await this.getClients();
    this.setAppState({ clients });
    return window.innerWidth > 700 && callback ? callback() : window.history.back();
  };

  /**
   * Get pt stats
   */
  private getStats: App.Methods["getStats"] = async (params, signal?) => {
    this.setLoadingState();
    const { stats } = await this.api.getStats(params, signal);
    this.state.events.stats = stats;
    this.clearLoadingFlag();
    return stats;
  };

  /**
   * Export client stats
   */
  private exportStats: App.Methods["exportStats"] = async (params) => {
    return await this.api.exportStats(params);
  };

  /* Exercises */

  /**
   * Get exercises
   */
  private getExercises = async (params?: { from?: number }) => {
    const exerciseListingResult = await this.api.getExercises(params);
    const { exercises, updated } = exerciseListingResult;
    const catalog = this.state.catalog;
    catalog.setExercises(exercises, updated);
    this.setState({ catalog });
    return exercises;
  };
  /**
   * Add exercises
   */
  private addExercises: App.Methods["addExercises"] = async (params) => {
    this.setLoadingState();
    await this.api.addExercises(params);
    return this.setAppState({
      modalTitle: i18next.t("Success"),
      showModal: true,
      modalMessage: i18next.t("Exercise added"),
    });
  };
  /**
   * Update exercises
   */
  private updateExercises: App.Methods["updateExercises"] = async (params) => {
    this.setLoadingState();
    await this.api.updateExercises(params);
    return this.setAppState({
      modalTitle: i18next.t("Success"),
      showModal: true,
      modalMessage: i18next.t("Exercise updated successfully"),
    });
  };
  /**
   * Add/Update Exercise Start / End Image
   */
  private updateExerciseImage: App.Methods["updateImage"] = async (newImage) => {
    const { image } = await this.api.updateImage(newImage);
    return image;
  };

  /* Workouts */

  /**
   * Get workouts
   */
  private getWorkouts: App.Methods["getWorkouts"] = async (params) => {
    const { workouts, updated } = await this.api.getWorkouts(params);
    this.state.catalog.setWorkouts(workouts, updated);
    return workouts;
  };

  /**
   * Add workouts
   */
  private addWorkouts: App.Methods["addWorkouts"] = async (params) => {
    await this.api.addWorkouts(params);
    const { workouts } = await this.api.getWorkouts();
    this.state.catalog.setWorkouts(workouts);
    return workouts;
  };
  /**
   * Update workouts
   */
  private updateWorkouts: App.Methods["updateWorkouts"] = async (params) => {
    await this.api.updateWorkouts(params);
    const { workouts } = await this.api.getWorkouts();
    this.state.catalog.setWorkouts(workouts);
    return workouts;
  };
  /**
   * Share workouts
   */
  private shareWorkouts: App.Methods["shareWorkouts"] = async (params) => {
    await this.api.shareWorkouts(params);
    const message = () => {
      if (params.to_self && params.to_customer_id) return "Workout sent to self and clients";
      else if (params.to_customer_id) return "Workout sent to clients";
      else if (params.to_self) return "Workout sent to self";
    };
    return message();
  };

  /* Schedule */

  /**
   * Get scheduled events
   */
  private getScheduledEvents: App.Methods["getScheduledEvents"] = async (params) => {
    const schedule = await this.api.getScheduledEvents(params);
    if (!schedule) return;
    this.state.events.schedule = { events: schedule }.events;
    return schedule;
  };
  /**
   * Update scheduled workout
   */
  private updateScheduledWorkout: App.Methods["updateScheduledWorkout"] = async (params) => {
    await this.api.updateScheduledWorkouts(params);
  };
  /**
   * Share session
   */
  private shareSession: App.Methods["shareSession"] = async (params) => {
    await this.api.shareSession(params);
  };

  /* Dashboard */

  /**
   * Get dashboard events
   */
  private getDashboardEvents: App.Methods["getDashboardEvents"] = async (params) => {
    const { events, start, end, event_name } = await this.api.getDashboardEvents(params);
    if (!events) return this.handleServerError(events);
    this.state.events.agenda = { events };
    return { events, start, end, event_name };
  };
  /**
   * Get session availability
   */
  private getSessionAvailability: App.Methods["getSessionAvailability"] = async (params) => {
    const schedule = await this.api.getSessionAvailability(params);
    if (!schedule) return;
    return schedule;
  };

  /* Contact Us */
  private contactUs: App.Methods["contactUs"] = async (params) => {
    const response = await this.api.contactUs(params);
    if (!response) return;
    return response;
  };
}

async function handleServerError(
  error: ApiError,
  setAppState: App.Methods["setAppState"],
  rollbarInstance: Rollbar
) {
  if (error.error_code === 401 || error.error_code === 402) {
    localStorage.removeItem("token");
    return setAppState({
      authenticated: false,
      user: undefined,
      companies: undefined,
      settings: undefined,
      modalMessage: `Error ${error.error_code}: ${i18next.t(`${error.error_message}`)}`,
      showModal: true,
      modalTitle: "Unauthorized",
    });
  }
  if (error.error_code === 20) {
    return setAppState({ showLoadingModal: false });
  }

  if (error.error_code === 404 || error.error_code === 400) {
    return setAppState({
      modalMessage: `${i18next.t(`${error.error_message}`)}`,
      modalTitle: `${i18next.t("Error")}!`,
      showModal: true,
    });
  }

  // log when we have no idea what this is
  rollbarInstance.error("An unknown network error occurred", error);

  return setAppState({
    modalMessage: `${i18next.t("Something went wrong")}!`,
    modalTitle: `${i18next.t("Error")}!`,
    showModal: true,
  });
}
